import { createSlice } from "@reduxjs/toolkit";
import {
  getBasePriceThunk,
  createBasePriceThunk,
  updateBasePriceThunk,
  deleteBasePriceThunk,
  getBasePriceByUnitsThunk,
  syncUnitSubtotalsThunk,
} from "state/action/base_price";

const parseKeyFromArg = (arg) => {
  const qp = (arg?.queryParams || "").replace(/^\?/, "");
  const params = new URLSearchParams(qp);
  const propertyId = params.get("property") || "ALL";
  const buildingId = params.get("building") || "ALL";
  return `${propertyId}|${buildingId}`;
};

const initialState = {
  getBasePrice: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },
  byKey: {},

  createBasePrice: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },

  updateBasePrice: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },

  deleteBasePrice: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },

  getBasePriceByUnits: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },

  syncUnitSubtotals: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },

  createBasePriceInputBody: {
    hierarchy_property: "",
    property: "",
    building: "",
    floor: "",
    unit: "",
    items: [
      {
        charge: "",
        currency: "IDR",
        price: "",
        measurement: "sqm", // Default
        frequency: "Monthly", // Default
      },
    ],
  },

  selectedBasePrice: {
    property: "",
    building: "",
    items: [],
  },

  base_price_object_value: "",
};

export const basePriceSlice = createSlice({
  name: "base_price",
  initialState,
  reducers: {
    setField: (state, action) => {
      const { field, value } = action.payload;
      state.createBasePriceInputBody[field] = value;
    },
    // Menambah baris item harga baru
    addItem: (state) => {
      state.createBasePriceInputBody.items.push({
        charge: "",
        currency: "IDR",
        price: "",
        measurement: "sqm", // Default
        frequency: "Monthly", // Default
      });
    },
    // Menghapus baris item harga
    removeItem: (state, action) => {
      const index = action.payload;
      state.createBasePriceInputBody.items.splice(index, 1);
    },
    // Update field di dalam specific item
    updateItem: (state, action) => {
      const { index, field, value } = action.payload;
      state.createBasePriceInputBody.items[index][field] = value;
    },
    resetForm: (state) => {
      state.createBasePriceInputBody = initialState.createBasePriceInputBody;
    },
    setBasePriceObjectValue: (state, action) => ({
      ...state,
      base_price_object_value: action.payload,
    }),

    setCreateBasePriceInputBody: (state, action) => ({
      ...state,
      createBasePriceInputBody: action.payload,
    }),

    setSelectedBasePrice: (state, action) => ({
      ...state,
      selectedBasePrice: action.payload,
    }),

    setClearBasePriceObjectValue: (state) => ({
      ...state,
      base_price_object_value: "",
    }),

    setClearCreateBasePriceInputBody: (state) => ({
      ...state,
      createBasePriceInputBody: {
        property: "",
        building: "",
        items: [],
      },
    }),

    setClearGetBasePrice: (state) => ({
      ...state,
      getBasePrice: {
        data: null,
        isFulfilled: false,
        isPending: false,
        isRejected: false,
        err: null,
      },
    }),

    setClearCreateBasePrice: (state) => ({
      ...state,
      createBasePrice: {
        data: null,
        isFulfilled: false,
        isPending: false,
        isRejected: false,
        err: null,
      },
    }),

    setClearUpdateBasePrice: (state) => ({
      ...state,
      updateBasePrice: {
        data: null,
        isFulfilled: false,
        isPending: false,
        isRejected: false,
        err: null,
      },
    }),

    setClearDeleteBasePrice: (state) => ({
      ...state,
      deleteBasePrice: {
        data: null,
        isFulfilled: false,
        isPending: false,
        isRejected: false,
        err: null,
      },
    }),

    setClearAllBasePriceState: (state) => ({
      ...state,
      getBasePrice: {
        data: null,
        isFulfilled: false,
        isPending: false,
        isRejected: false,
        err: null,
      },
      byKey: {},
      createBasePrice: {
        data: null,
        isFulfilled: false,
        isPending: false,
        isRejected: false,
        err: null,
      },
      updateBasePrice: {
        data: null,
        isFulfilled: false,
        isPending: false,
        isRejected: false,
        err: null,
      },
      deleteBasePrice: {
        data: null,
        isFulfilled: false,
        isPending: false,
        isRejected: false,
        err: null,
      },
      base_price_object_value: "",
      selectedBasePrice: null,
    }),

    setClearBasePriceByKey: (state, action) => {
      const key = action.payload;
      const clone = { ...(state.byKey || {}) };
      delete clone[key];
      return { ...state, byKey: clone };
    },
  },
  extraReducers(builder) {
    builder

      .addCase(getBasePriceThunk.pending, (state) => ({
        ...state,
        getBasePrice: {
          data: null,
          isPending: true,
          isFulfilled: false,
          isRejected: false,
          err: null,
        },
      }))
      .addCase(getBasePriceThunk.fulfilled, (state, action) => {
        const next = {
          ...state,
          getBasePrice: {
            data: action.payload,
            isPending: false,
            isFulfilled: true,
            isRejected: false,
            err: null,
          },
        };
        try {
          const key = parseKeyFromArg(action.meta?.arg);
          next.byKey = {
            ...(state.byKey || {}),
            [key]: action.payload,
          };
        } catch (_) {}
        return next;
      })
      .addCase(getBasePriceThunk.rejected, (state, action) => ({
        ...state,
        getBasePrice: {
          data: null,
          isPending: false,
          isFulfilled: false,
          isRejected: true,
          err: action.error.message,
        },
      }))

      .addCase(createBasePriceThunk.pending, (state) => ({
        ...state,
        createBasePrice: {
          data: null,
          isPending: true,
          isFulfilled: false,
          isRejected: false,
          err: null,
        },
      }))
      .addCase(createBasePriceThunk.fulfilled, (state, action) => ({
        ...state,
        createBasePrice: {
          data: action.payload,
          isPending: false,
          isFulfilled: true,
          isRejected: false,
          err: null,
        },
      }))
      .addCase(createBasePriceThunk.rejected, (state, action) => ({
        ...state,
        createBasePrice: {
          data: null,
          isPending: false,
          isFulfilled: false,
          isRejected: true,
          err: action.error.message,
        },
      }))

      .addCase(updateBasePriceThunk.pending, (state) => ({
        ...state,
        updateBasePrice: {
          data: null,
          isPending: true,
          isFulfilled: false,
          isRejected: false,
          err: null,
        },
      }))
      .addCase(updateBasePriceThunk.fulfilled, (state, action) => ({
        ...state,
        updateBasePrice: {
          data: action.payload,
          isPending: false,
          isFulfilled: true,
          isRejected: false,
          err: null,
        },
      }))
      .addCase(updateBasePriceThunk.rejected, (state, action) => ({
        ...state,
        updateBasePrice: {
          data: null,
          isPending: false,
          isFulfilled: false,
          isRejected: true,
          err: action.error.message,
        },
      }))

      .addCase(deleteBasePriceThunk.pending, (state) => ({
        ...state,
        deleteBasePrice: {
          data: null,
          isPending: true,
          isFulfilled: false,
          isRejected: false,
          err: null,
        },
      }))
      .addCase(deleteBasePriceThunk.fulfilled, (state, action) => ({
        ...state,
        deleteBasePrice: {
          data: action.payload,
          isPending: false,
          isFulfilled: true,
          isRejected: false,
          err: null,
        },
      }))
      .addCase(deleteBasePriceThunk.rejected, (state, action) => ({
        ...state,
        deleteBasePrice: {
          data: null,
          isPending: false,
          isFulfilled: false,
          isRejected: true,
          err: action.error.message,
        },
      }))

      .addCase(getBasePriceByUnitsThunk.pending, (state) => ({
        ...state,
        getBasePriceByUnits: {
          data: null,
          isPending: true,
          isFulfilled: false,
          isRejected: false,
          err: null,
        },
      }))
      .addCase(getBasePriceByUnitsThunk.fulfilled, (state, action) => ({
        ...state,
        getBasePriceByUnits: {
          data: action.payload,
          isPending: false,
          isFulfilled: true,
          isRejected: false,
          err: null,
        },
      }))
      .addCase(getBasePriceByUnitsThunk.rejected, (state, action) => ({
        ...state,
        getBasePriceByUnits: {
          data: null,
          isPending: false,
          isFulfilled: false,
          isRejected: true,
          err: action.error.message,
        },
      }))

      .addCase(syncUnitSubtotalsThunk.pending, (state) => ({
        ...state,
        syncUnitSubtotals: {
          data: null,
          isPending: true,
          isFulfilled: false,
          isRejected: false,
          err: null,
        },
      }))
      .addCase(syncUnitSubtotalsThunk.fulfilled, (state, action) => ({
        ...state,
        syncUnitSubtotals: {
          data: action.payload,
          isPending: false,
          isFulfilled: true,
          isRejected: false,
          err: null,
        },
      }))
      .addCase(syncUnitSubtotalsThunk.rejected, (state, action) => ({
        ...state,
        syncUnitSubtotals: {
          data: null,
          isPending: false,
          isFulfilled: false,
          isRejected: true,
          err: action.error.message,
        },
      }));
  },
});

export const basePriceAction = {
  ...basePriceSlice.actions,
  getBasePriceThunk,
  createBasePriceThunk,
  updateBasePriceThunk,
  deleteBasePriceThunk,
  getBasePriceByUnitsThunk,
  syncUnitSubtotalsThunk,
};

export function selectBasePriceObjectValue(state) {
  return state?.base_price?.base_price_object_value;
}

export function selectBasePrice(state) {
  return state?.base_price;
}

export const selectBasePricesByKey = (propertyId, buildingId) => (state) => {
  const key = `${propertyId || "ALL"}|${buildingId || "ALL"}`;

  return state?.base_price?.byKey?.[key]?.data?.data || [];
};

export default basePriceSlice.reducer;
